/* eslint-disable @stencil-community/required-jsdoc */
import { Component, Host, h, Prop, State, Listen } from '@stencil/core';
import { Memoize, isEmpty, parseIfString } from '@springbokagency/stencil-base';
import checkWindowSize from '../../../utils/checkWindowSize';
import { ArticleModalData } from './article-modal.types';
import pageTracking from '../../../utils/pageTracking';
import state from '../../../store/store';

@Component({
  tag: 'efb-article-modal',
  styleUrl: 'article-modal.scss',
  shadow: true,
})
export class EfbArticleModal {
  private modalRef: HTMLEfbBaseModalElement;

  /**
   * State
   */

  @State() item: ArticleModalData | undefined;

  @State() isMobile: boolean = checkWindowSize().isMobile;

  @State() mainContent: string;

  /**
   * Props
   */

  @Prop() data: Array<ArticleModalData> | string;

  @Memoize('data')
  get parsedData(): Array<ArticleModalData> {
    return parseIfString(this.data);
  }

  @Prop() activeItem: string;

  /**
   * Events
   */

  @Listen('resize', { target: 'window' })
  handleWindowResize() {
    this.isMobile = checkWindowSize().isMobile;
  }

  /**
   * Methods
   */

  private handleCloseClick() {
    this.modalRef.close();
  }

  private findItem(id: string) {
    const data = this.parsedData;

    if (isEmpty(data)) {
      return null;
    }

    let item = data[0];

    if (!isEmpty(id)) {
      const findData = data.find((data_item) => data_item.id === id);

      item = findData;
    }

    this.item = item;

    return null;
  }

  private renderMainImageMobile() {
    const image = this.item.mainImage;

    return `<picture>
        ${
          image.pathWebp &&
          `<source
            srcSet="${image.pathWebp} ${
            !isEmpty(image.pathWebp2x) ? `, ${image.pathWebp2x} 2x` : ''
          }"
            type="image/webp"
          />`
        }
        <img
          class="main-image-mobile"
          src=${image.path}
          srcset="${!isEmpty(image.path2x) ? `${image.path2x} 2x` : ''}"
          alt="${image.alt}"
          width="${image.width}"
          height="${image.height}"
          loading="lazy"
        />
      </picture>`;
  }

  private renderMainContent() {
    const regex = /::mobile-image-position::/g;

    if (!this.isMobile) this.mainContent = this.item.content.replace(regex, '');

    // Place sidebar's main image inside the content on mobile
    this.mainContent = this.item.content.replace(
      regex,
      !isEmpty(this.item.mainImage) ? this.renderMainImageMobile() : ''
    );

    return null;
  }

  /**
   * Lifecycle Hooks
   */

  componentWillLoad() {
    // define active item
    this.findItem(this.activeItem);

    // render main content
    this.renderMainContent();

    // trigger manually page tracking
    pageTracking(state.currentLanguage, this.item?.pageAnalyticsId);
  }

  /**
   * Render
   */

  render() {
    return (
      <Host>
        <efb-base-modal
          ref={(el) => {
            this.modalRef = el;
          }}
          modalId="article-modal"
        >
          <div class="article-modal">
            {/* Backdrop */}
            <span
              class="backdrop"
              aria-hidden="true"
              onClick={() => this.handleCloseClick()}
            ></span>

            {/* Close button */}
            <div class="modal-header">
              <button class="close-button" onClick={() => this.handleCloseClick()}>
                <efb-icon name="close"></efb-icon>
              </button>
            </div>

            {/* Content */}
            {!isEmpty(this.item) && (
              <div class="content-wrapper">
                <article class="content">
                  {/* Header */}
                  <header class={`content-header is-${this.item.variant}-variant`}>
                    <div
                      class={`header-text ${isEmpty(this.item.headerImg) ? 'has-text-only' : ''}`}
                    >
                      {/* Title */}
                      {(this.item.title || this.item.subtitle) && (
                        <h2>
                          <span class="title" innerHTML={this.item.title}></span>
                          <span class="subtitle" innerHTML={this.item.subtitle}></span>
                        </h2>
                      )}
                    </div>

                    {/* Header's image */}
                    {this.item.headerImg && (
                      <div class="header-image">
                        <efb-img {...this.item.headerImg} />
                      </div>
                    )}
                  </header>

                  {/* Article */}
                  <main class="content-main">
                    {/* Text */}
                    {this.item.content && (
                      <div class="main-text" innerHTML={this.mainContent}></div>
                    )}

                    {/* Main's image / desktop */}
                    {!this.isMobile && this.item.mainImage && (
                      <div class="main-image-wrapper">
                        <efb-img class="main-image" {...this.item.mainImage} />
                      </div>
                    )}
                  </main>

                  {/* Footer */}
                  {this.item.modalFooter && (
                    <footer class="content-footer">
                      <div class="text-wrapper">
                        <efb-title text={this.item.modalFooter.title} size="sm" />
                        <efb-paragraph text={this.item.modalFooter.text} />
                        <efb-button
                          {...this.item.modalFooter.button}
                          target="_blank"
                          variant="purple"
                        />
                      </div>
                      <div class="image-wrapper">
                        <efb-img {...this.item.modalFooter.img} />
                      </div>
                    </footer>
                  )}
                </article>
              </div>
            )}
          </div>
        </efb-base-modal>
      </Host>
    );
  }
}
